// Mapa en vivo: recibe ubicaciones por WebSocket y las muestra. Ver CLAUDE.md §7.
// El TURNO vive en el servidor: al conectar llega la última posición de cada
// técnico con turno activo (aunque lleve rato sin reportar — un técnico quieto
// con pantalla apagada calla; el motor nativo emite al moverse). Cada renglón
// muestra la antigüedad de su última señal. "Detener turno" lo quita al instante.
import { useCallback, useEffect, useState } from 'react'
import { MapView } from '@/components/MapView'
import { useLocationsSocket } from '@/hooks/useWebSocket'
import { useLocationStore } from '@/store/locationStore'

interface SnapshotMsg {
  type?: string
  user_id?: string
  name?: string
  lat?: number
  lng?: number
  accuracy?: number | null
  timestamp?: string
}

function ageLabel(ts: string, now: number): string {
  const m = Math.max(0, Math.round((now - Date.parse(ts)) / 60000))
  if (m < 1) return 'ahora'
  if (m < 60) return `hace ${m} min`
  const h = Math.floor(m / 60)
  return `hace ${h} h ${String(m % 60).padStart(2, '0')} min`
}

export default function LiveMap() {
  const { locations, upsert, remove } = useLocationStore()
  const [now, setNow] = useState(() => Date.now())

  const onMessage = useCallback(
    (msg: unknown) => {
      const m = msg as SnapshotMsg
      if (m.type === 'location_offline' && m.user_id) {
        remove(m.user_id)
        return
      }
      if (m.type === 'location_snapshot' && m.user_id && typeof m.lat === 'number' && typeof m.lng === 'number') {
        upsert({
          user_id: m.user_id,
          name: m.name ?? '',
          lat: m.lat,
          lng: m.lng,
          accuracy: m.accuracy ?? null,
          timestamp: m.timestamp ?? new Date().toISOString(),
        })
      }
    },
    [upsert, remove],
  )

  useLocationsSocket(onMessage, true)

  // Refresca las etiquetas de antigüedad cada 30s.
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(iv)
  }, [])

  const list = Object.values(locations)

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Mapa en vivo</h1>
        <span className="rounded-full bg-ds-50 px-3 py-1 text-sm font-medium text-ds-700">
          {list.length} en turno
        </span>
      </div>

      <div className="isolate h-[62vh] overflow-hidden rounded-xl border border-gray-200">
        <MapView locations={list} />
      </div>

      {list.length === 0 ? (
        <p className="mt-3 text-sm text-gray-400">
          Nadie tiene turno activo en este momento. Los técnicos aparecen al iniciar
          turno desde su agenda y se quitan al detenerlo.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
          {list.map((l) => {
            const fresh = now - Date.parse(l.timestamp) < 3 * 60_000
            return (
              <li key={l.user_id} className="flex items-center justify-between p-3 text-sm">
                <span className="flex items-center gap-2 font-medium text-gray-800">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${
                      fresh ? 'animate-pulse bg-green-500' : 'bg-amber-400'
                    }`}
                  />
                  {l.name || l.user_id}
                </span>
                <span className={fresh ? 'text-green-600' : 'text-gray-500'}>
                  {ageLabel(l.timestamp, now)}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
